import { useCallback, useEffect, useRef, useState } from 'react';
import { BatchStatusEnum } from '../../enums/BatchStatusEnum';
import { WebSocketStatusEnum } from '../../enums/WebSocketStatusEnum';
import type { Batch } from '../../interfaces/Batch';
import { authService } from '../../services/authService';
import { BatchService } from '../../services/batchApiService';
import { useAppStore } from '../../store/store';
import { hasFileAccessScope } from '../../utils/jwt';
import { useAuth } from '../useAuth';
import { useWebSocketConnectionStatus } from '../useWebSocketConnectionStatus';

const batchService = new BatchService();

function mapAiBatchStatusToEnum(batchStatus?: string): BatchStatusEnum | undefined {
  switch (batchStatus?.trim().toLowerCase()) {
    case 'processing':
      return BatchStatusEnum.Processing;
    case 'success':
      return BatchStatusEnum.Completed;
    case 'failed':
      return BatchStatusEnum.Failed;
    case 'canceled':
      return BatchStatusEnum.Cancelled;
    default:
      return undefined;
  }
}

/**
 * Custom hook for managing batch operations
 *
 * @returns {object} Batch-related state and functions
 */
export function useBatches() {
  const [activeBatches, setActiveBatches] = useState<Batch[]>([]);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const { isLoggedIn } = useAuth();
  const webSocketStatus = useWebSocketConnectionStatus();
  const lastEventTsByBatchIdRef = useRef<Map<number, number>>(new Map());

  const {
    startPolling: startBatchesPolling,
    stopPolling: stopBatchesPolling,
    isPolling: isBatchesPolling,
    activeBatchesCount,
    setActiveBatchesCount,
  } = useAppStore((s) => s.appEvents.batchesPolling);

  /**
   * Fetch active batches (Queued or Processing) and return the running count
   */
  const fetchActiveBatches = useCallback(async (): Promise<number> => {
    // File-scoped OTP / anticapture sessions: /batches not on allow-list → 403 DENIED.
    if (!isLoggedIn || hasFileAccessScope(authService.getToken())) {
      setActiveBatches([]);
      setActiveBatchesCount(0);
      return 0;
    }

    try {
      setIsLoadingBatches(true);
      const response = await batchService.getBatches({
        offset: 0,
        count: 50,
        status: undefined,
      });

      const active = response.batches.filter(
        (batch) =>
          batch.status === BatchStatusEnum.Queued ||
          batch.status === BatchStatusEnum.Processing ||
          batch.status === BatchStatusEnum.Failed ||
          batch.status === BatchStatusEnum.PartiallyCompleted,
      );

      setActiveBatches(active);

      const runningCount = response.batches.filter(
        (batch) =>
          batch.status === BatchStatusEnum.Queued || batch.status === BatchStatusEnum.Processing,
      ).length;
      // Header spinner uses this count; must update even when WS is Open (no polling).
      setActiveBatchesCount(runningCount);
      return runningCount;
    } catch (error) {
      console.error('Error fetching active batches:', error);
      setActiveBatches([]);
      setActiveBatchesCount(0);
      return 0;
    } finally {
      setIsLoadingBatches(false);
    }
  }, [isLoggedIn, setActiveBatchesCount]);

  const syncPolling = useCallback(
    (runningCount: number) => {
      // Polling is a fallback only when EventHub is not Open.
      const shouldPoll =
        isLoggedIn && runningCount > 0 && webSocketStatus !== WebSocketStatusEnum.Open;

      if (shouldPoll && !isBatchesPolling) {
        startBatchesPolling(
          fetchActiveBatches,
          () => {},
          () => {},
        );
        return;
      }

      if (!shouldPoll && isBatchesPolling) {
        stopBatchesPolling();
      }
    },
    [
      fetchActiveBatches,
      isBatchesPolling,
      isLoggedIn,
      startBatchesPolling,
      stopBatchesPolling,
      webSocketStatus,
    ],
  );

  /**
   * Initial fetch + polling gated on WS status.
   */
  useEffect(() => {
    if (!isLoggedIn) {
      if (isBatchesPolling) {
        stopBatchesPolling();
      }
      setActiveBatches([]);
      setActiveBatchesCount(0);
      lastEventTsByBatchIdRef.current.clear();
      return;
    }

    let cancelled = false;

    fetchActiveBatches().then((count) => {
      if (cancelled) return;
      syncPolling(count);
    });

    return () => {
      cancelled = true;
      if (isBatchesPolling) {
        stopBatchesPolling();
      }
    };
  }, [
    isLoggedIn,
    fetchActiveBatches,
    isBatchesPolling,
    stopBatchesPolling,
    setActiveBatchesCount,
    syncPolling,
    webSocketStatus,
  ]);

  /**
   * Manual refresh function that also starts polling if needed
   */
  const refreshBatches = useCallback(async () => {
    const count = await fetchActiveBatches();
    syncPolling(count);
  }, [fetchActiveBatches, syncPolling]);

  // Absolute progress from AI classification realtime events (last write wins; event_ts ordered).
  useEffect(() => {
    return useAppStore.getState().events.on.batchProgressUpdated((payload) => {
      const {
        batchId,
        processed,
        total,
        pending,
        processing,
        succeeded,
        failed,
        canceled,
        eventTs,
        batchStatus,
      } = payload;

      if (typeof eventTs === 'number') {
        const previousTs = lastEventTsByBatchIdRef.current.get(batchId);
        if (previousTs != null && eventTs <= previousTs) {
          return;
        }
        lastEventTsByBatchIdRef.current.set(batchId, eventTs);
      }

      const mappedStatus = mapAiBatchStatusToEnum(batchStatus);
      setActiveBatches((prev) => {
        const next = prev.map((batch) => {
          if (batch.id !== batchId) {
            return batch;
          }
          return {
            ...batch,
            processedFiles: typeof succeeded === 'number' ? succeeded : processed,
            totalFiles: total,
            ...(typeof pending === 'number' ? { pendingFiles: pending } : {}),
            ...(typeof processing === 'number' ? { processingFiles: processing } : {}),
            ...(typeof failed === 'number' ? { failedFiles: failed } : {}),
            ...(typeof canceled === 'number' ? { canceledFiles: canceled } : {}),
            ...(mappedStatus ? { status: mappedStatus } : {}),
          };
        });
        const runningCount = next.filter(
          (batch) =>
            batch.status === BatchStatusEnum.Queued || batch.status === BatchStatusEnum.Processing,
        ).length;
        useAppStore.getState().appEvents.batchesPolling.setActiveBatchesCount(runningCount);
        return next;
      });
    });
  }, []);

  return {
    activeBatches,
    activeCount: activeBatchesCount,
    isLoadingBatches,
    isPolling: isBatchesPolling,
    refreshBatches,
  };
}

export default useBatches;
